import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/api-client';
import { createPageUrl } from '@/utils';

interface ApiError extends Error {
  status?: number;
}

interface ClientFunctionsApi {
  invoke<T = unknown>(name: string, payload?: unknown): Promise<{ data: T }>;
}

export interface ClientPortalUser {
  email?: string;
  name?: string;
  // The clientMe payload is intentionally loose — depends on which fields
  // the legacy clientMe function returns; treated as opaque by callers.
  [key: string]: unknown;
}

export function useClientAuth() {
  const queryClient = useQueryClient();

  const { data: client, isLoading, error } = useQuery<ClientPortalUser, ApiError>({
    queryKey: ['client-me'],
    queryFn: async () => {
      const { data } = await (api as { functions: ClientFunctionsApi }).functions.invoke<ClientPortalUser>('clientMe');
      return data;
    },
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (error && error.status === 401) {
      window.location.href = createPageUrl('Login');
    }
  }, [error]);

  const logout = async () => {
    try {
      await (api as { functions: ClientFunctionsApi }).functions.invoke('clientLogout');
    } catch {
      // ignore
    }
    queryClient.removeQueries({ queryKey: ['client-me'] });
    window.location.href = createPageUrl('Login');
  };

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['client-me'] });
  };

  return { client, isLoading, error, logout, invalidate };
}
